const domain = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN;
const token = import.meta.env.VITE_SHOPIFY_STOREFRONT_TOKEN;
const apiVersion = import.meta.env.VITE_SHOPIFY_API_VERSION || "2026-07";
const fallbackDomain = "i-hate-shirts.myshopify.com";

export const hasShopifyConfig = Boolean(domain && token);
export const shopifyDomain = domain || fallbackDomain;

type ShopifyCartLine = {
  variantId: string;
  quantity: number;
};

async function shopifyFetch<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  if (!hasShopifyConfig) {
    throw new Error("Missing Shopify Storefront API config.");
  }

  const response = await fetch(`https://${domain}/api/${apiVersion}/graphql.json`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": token
    },
    body: JSON.stringify({ query, variables })
  });

  if (!response.ok) {
    throw new Error(`Shopify request failed with ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export async function createShopifyCheckout(lines: ShopifyCartLine[]): Promise<string> {
  const query = `
    mutation CreateCart($lines: [CartLineInput!]!) {
      cartCreate(input: { lines: $lines }) {
        cart {
          checkoutUrl
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const payload = await shopifyFetch<{
    data?: { cartCreate?: { cart?: { checkoutUrl?: string }; userErrors?: { message: string }[] } };
  }>(query, {
    lines: lines.map((line) => ({
      merchandiseId: line.variantId,
      quantity: line.quantity
    }))
  });

  const checkoutUrl = payload.data?.cartCreate?.cart?.checkoutUrl;
  if (!checkoutUrl) {
    const error = payload.data?.cartCreate?.userErrors?.[0]?.message || "Checkout URL was not returned.";
    throw new Error(error);
  }

  return checkoutUrl;
}

export function createCartPermalink(lines: ShopifyCartLine[]): string {
  const lineItems = lines.map((line) => `${line.variantId}:${line.quantity}`).join(",");
  return `https://${shopifyDomain}/cart/${lineItems}`;
}
